'use client';

import debounce from 'lodash.debounce';
import { PropsWithChildren, useCallback, useEffect, useRef } from 'react';
import PageSectionType from '@alltrails/amplitude/enums/PageSectionType';
import logWebSectionViewed from '@alltrails/amplitude/events/logWebSectionViewed';
import { OVERVIEW_SECTION_ID, REVIEWS_SECTION_ID } from '@/utils/constants';
import { useAppDispatch, useAppSelector } from '@/utils/redux';
import SectionNavId from '@/utils/SectionNavId';
import { setActiveSection } from '@/utils/trailPageSlice';

const TOP_OF_PAGE = 164; // 100 padding + 64 sticky nav height

type SectionViewedHandlerProps = {
  className?: string;
  id?: SectionNavId;
  sectionType?: PageSectionType;
};

const SectionViewedHandler = ({ children, className, id, sectionType }: PropsWithChildren<SectionViewedHandlerProps>) => {
  const dispatch = useAppDispatch();
  const isScrollingViaTabs = useAppSelector(state => state.trailPage.isScrollingViaTabs);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element || !sectionType) {
      return;
    }

    let timer: ReturnType<typeof setTimeout> | undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          clearTimeout(timer);
          return;
        }

        // only log the section as viewed once it has been on screen for a second
        timer = setTimeout(() => {
          observer.disconnect();
          logWebSectionViewed({ page_type: 'trail page', section_type: sectionType });
        }, 1000);
      },
      { threshold: 0.2 }
    );

    observer.observe(element);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [sectionType]);

  const handleScroll = useCallback(() => {
    const refRect = ref.current?.getBoundingClientRect();
    if (!refRect) {
      return;
    }
    const { bottom, top } = refRect;

    // https://alltrails.atlassian.net/browse/DISCO-11047
    // This fixes a bug where clicking on a tab causes the page to scroll past other sections,
    // which update activeSection, causing the indicator on the tab bar to jump around.
    if (isScrollingViaTabs) {
      return;
    }

    if (id && (Math.abs(top) <= TOP_OF_PAGE || (top < 0 && bottom > TOP_OF_PAGE))) {
      dispatch(setActiveSection(id));
    } else if (id === OVERVIEW_SECTION_ID && top > TOP_OF_PAGE) {
      dispatch(setActiveSection(OVERVIEW_SECTION_ID));
    }
  }, [dispatch, id, isScrollingViaTabs]);

  useEffect(() => {
    const debouncedScroll = debounce(handleScroll, 300);
    window.addEventListener('scroll', debouncedScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', debouncedScroll);
      debouncedScroll.cancel();
    };
  }, [handleScroll]);

  return (
    <div ref={ref} className={className} id={id} tabIndex={id === REVIEWS_SECTION_ID ? -1 : undefined}>
      {children}
    </div>
  );
};

export default SectionViewedHandler;
